import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import CropModel from '@dehaat/kisan-app-bl/models/CropV2';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect, useRef, useState } from 'react';
import CropStageAccordian from '@/components/cropGuide/CropStageAccordian';
import ErrorModal from '@/components/ErrorModal';
import HeaderWithBackButton from '@/components/HeaderWithBackButton';
import AddDate from '@/components/icons/AddDate';
import EditProfileIcon from '@/components/icons/EditProfileIcon';
import PlusIcon from '@/components/icons/PlusIcon';
import LazyImage from '@/components/LazyImage';
import Okay from '@/components/modalActions/Okay';
import Paragraph, { VARIANT } from '@/components/Paragraph';
import Spinner from '@/components/Spinner';
import SubHeading from '@/components/SubHeading';
import { ACCESS_TOKEN_KEY, NAVIGATION_FROM_APP, USER_CROPS_KEY, USER_SOWING_DATES_KEY } from '@/constants/common';
import { ACTION_TAG } from '@/constants/crop';
import { AuthContext } from '@/context/AuthProvider';
import useErrorModalState from '@/hooks/useErrorModalState';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import CropStageImage from '@/images/cropGuide/cropStage.webp';
import { CropAction, CropStage, CurrentAction, SowingDateResObj } from '@/models/cropGuide';
import styles from '@/styles/HideScrollbar.module.css';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import fetchCropsV2 from '@/utils/api/fetchCropsV2';
import getCropActions from '@/utils/api/getCropActions';
import getCropStages from '@/utils/api/getCropStages';
import getSowingDates from '@/utils/api/getSowingDate';
import { getLocalStorageKey, isArray } from '@/utils/helper';
import formatDate from '@/utils/helpers/formatDate';
const SowingModal = dynamic(() => import('@/components/cropGuide/SowingModal'));
interface CropProps {
  userCrop: CropModel;
  cropId: number;
  onClick: VoidFunction;
}
const Crop = ({
  userCrop,
  cropId,
  onClick
}: CropProps) => {
  return <div key={userCrop.id} onClick={onClick} className={`relative rounded-[40px_40px_36px_36px] ml-2 p-1.5 flex flex-col items-center ${userCrop.id === cropId ? 'bg-primary-100 text-white' : ''}`} data-sentry-component="Crop" data-sentry-source-file="crop-guide.tsx">
      <div className="border border-neutral-40 h-14 overflow-hidden relative rounded-full w-14">
        <LazyImage alt={userCrop.app_display_name} fallBackIcon={<div className="bg-neutral-300 h-14 w-14 flex items-center justify-center overflow-hidden rounded-full text-xl">
              <span>{userCrop.app_display_name[0]}</span>
            </div>} imageUrl={userCrop.image_url} className="h-14 w-14 rounded-full" data-sentry-element="LazyImage" data-sentry-source-file="crop-guide.tsx" />
      </div>
      <Paragraph variant={VARIANT.MEDIUM} className="mt-0.5 text-center p-1 mb-2" data-sentry-element="Paragraph" data-sentry-source-file="crop-guide.tsx">
        {userCrop.app_display_name}
      </Paragraph>
      {userCrop.id === cropId && <div className="bg-white h-1 w-11 rounded-[4px_4px_0px_0px] absolute bottom-3" />}
    </div>;
};
interface Props {
  crops: CropModel[];
  fromApp: boolean;
  currentCrop: CropModel | null;
}
const CropGuidePage = ({
  crops,
  fromApp,
  currentCrop
}: Props) => {
  const {
    t
  } = useTranslation('cropGuide');
  const {
    isAuthenticated
  } = useContext(AuthContext);
  const {
    locale,
    push,
    replace,
    query
  } = useRouter();
  const {
    errorModalState,
    hideErrorModal,
    setErrorModalState
  } = useErrorModalState();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [userCrops, setUserCrops] = useState<CropModel[]>([]);
  const [crop, setCrop] = useState<CropModel | null>(currentCrop);
  const [cropActions, setCropActions] = useState<CropAction[]>([]);
  const [cropStages, setCropStages] = useState<CropStage[]>([]);
  const [showSowingModal, setShowSowingModal] = useState(false);
  const [sowingDate, setSowingDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dates, setDates] = useState<SowingDateResObj[]>([]);
  const [currentAction, setCurrentAction] = useState<CurrentAction>({
    id: -1,
    action: null
  });
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const handleCropContainerScroll = () => {
    //to scroll container to the start where current crop is moved after selection
    const container = scrollContainerRef.current;
    if (container && isAuthenticated) container.scrollBy({
      left: -container.scrollLeft,
      behavior: 'smooth'
    });
  };
  const fetchCropData = async () => {
    setIsLoading(true);
    try {
      if (crop) {
        const [stages, actions] = await Promise.all([getCropStages(crop.id as number, locale || DEFAULT_LANG), !isAuthenticated && sowingDate ? getCropActions(crop.id as number, locale || DEFAULT_LANG, sowingDate) : getCropActions(crop.id as number, locale || DEFAULT_LANG)]);
        setCropActions(actions);
        setCropStages(stages);
        setIsLoading(false);
      }
    } catch {
      setIsLoading(false);
      setErrorModalState({
        ...errorModalState,
        show: true
      });
    }
  };
  useEffect(() => {
    if (crops.length === 0 && !isAuthenticated) {
      const anomUserCrops: CropModel[] | undefined = getLocalStorageKey(USER_CROPS_KEY);
      if (anomUserCrops && anomUserCrops.length > 0) {
        setUserCrops(anomUserCrops);
        const crop = anomUserCrops.find(crop => crop.id === Number(query.crop));
        if (crop) {
          setCrop(crop);
        } else {
          setCrop(anomUserCrops[0]);
        }
      } else {
        push('/crop?redirect_uri=/crop-guide');
      }
    }
  }, [isAuthenticated, crops, push, query]);
  useEffect(() => {
    if (crops.length === 0 && isAuthenticated) {
      push('/crop?redirect_uri=/crop-guide');
    }
  }, [isAuthenticated, push, crops]);
  useEffect(() => {
    if (isArray(dates) && crop) {
      const date = dates.find(date => date.crop_id === crop.id);
      setSowingDate(date && date.time || null);
    }
  }, [crop, dates]);
  useEffect(() => {
    if (cropActions.length > 0 && sowingDate) {
      const action = cropActions.find(({
        action_tag
      }) => action_tag === ACTION_TAG.CURRENT || action_tag === ACTION_TAG.UPCOMING);
      if (action) {
        setCurrentAction({
          id: action.id,
          action: action.action_type
        });
      }
    }
  }, [cropActions, sowingDate]);
  useEffect(() => {
    fetchCropData();
    /* do not include fetchCropData in dependencies
     as it leads to unnecessary re-renders */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sowingDate, dates]);
  useEffect(() => {
    const fetchCropDates = async () => {
      try {
        if (isAuthenticated) {
          const allDates = await getSowingDates();
          setDates(allDates);
        } else {
          const dates: SowingDateResObj[] | undefined = getLocalStorageKey(USER_SOWING_DATES_KEY);
          setDates(dates || []);
        }
      } catch {
        setIsLoading(false);
        setErrorModalState({
          ...errorModalState,
          show: true
        });
      }
    };
    fetchCropDates();
    /* do not include errorModalState and setErrorModalState in dependencies
     as we don't want to call fetchCropDates again if error happens */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crop, isAuthenticated]);
  useEffect(() => {
    const anomUserCrops: CropModel[] | undefined = getLocalStorageKey(USER_CROPS_KEY);
    if (anomUserCrops) {
      const uniqueAnomUserCrops = anomUserCrops.filter(crop => !crops.find(({
        id
      }) => id === crop.id));
      setUserCrops([...crops, ...uniqueAnomUserCrops]);
    } else {
      setUserCrops(crops);
    }
  }, [crops]);
  useEffect(() => {
    if (crop && sowingDate) {
      const eventParams = {
        Crop_Name: crop.name,
        Crop_ID: crop.id,
        Sowing_Date: formatDate(sowingDate, {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      };
      const timer = setTimeout(() => {
        trackCustomEvent(CustomEventTypes.CROP_GUIDE_USER_LANDED, JSON.stringify(eventParams));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [crop, sowingDate]);
  return crop ? <main className={`h-screen relative ${fromApp && !showSowingModal ? 'pt-0' : 'pt-16'}`} data-sentry-component="CropGuidePage" data-sentry-source-file="crop-guide.tsx">
      {query.sh != 'f' && showSowingModal && <HeaderWithBackButton showHomeIcon={!fromApp} showProfileIcon={!fromApp} heading={showSowingModal ? t('crop_sowing_date') : `${crop.app_display_name} ${t('crop_guide')}`} onBackClick={showSowingModal ? () => setShowSowingModal(false) : undefined} />}
      {showSowingModal ? <SowingModal crop={crop} onModalClose={date => {
      setSowingDate(date);
      setShowSowingModal(false);
    }} sowingDate={sowingDate} /> : <section className="relative m-auto max-w-7xl w-full pb-24">
          <div className={`flex bg-neutral-10 ${fromApp ? 'pt-1' : 'pt-4'} pl-6`}>
            <Link href="/crop?redirect_uri=/crop-guide" className="mt-2 mr-4" onClick={() => trackCustomEvent(CustomEventTypes.CROP_GUIDE_ADD_CROPS_CLICKED)}>
              <div className="h-12 w-12 rounded-full bg-white border border-primary-100 m-auto">
                <PlusIcon width={14} height={14} className="fill-transparent stroke-primary-100 m-4" />
              </div>
              <Paragraph variant={VARIANT.MEDIUM} className="text-primary-100 my-3 text-center">
                {t('add_crops')}
              </Paragraph>
            </Link>
            <div ref={scrollContainerRef} className={`flex overflow-x-scroll ${styles.scroll}`}>
              {userCrops.filter(crop => crop.is_khetibook_enabled).map(userCrop => <Crop key={userCrop.id} userCrop={userCrop} cropId={crop.id} onClick={() => {
            setCrop(userCrop);
            handleCropContainerScroll();
            replace({
              pathname: '/crop-guide',
              query: {
                crop: userCrop.id
              }
            });
          }} />)}
            </div>
          </div>

          {sowingDate ? <div className="bg-white relative -top-3 h-10 py-4 px-6 flex items-center">
              <Paragraph className="text-neutral-70" variant={VARIANT.MEDIUM}>
                {t('sowing_date')}:
              </Paragraph>
              <div onClick={() => setShowSowingModal(true)} className="flex items-center gap-1.5 ml-1">
                <SubHeading className="text-primary-100 text-sm">
                  {formatDate(sowingDate, {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
                </SubHeading>

                <EditProfileIcon className="cursor-pointer w-5 stroke-primary-100 stroke-1.5 fill-primary-100" />
              </div>
            </div> : <div className="py-4 px-6 bg-[#F7FFE6] border border-[#AECC70] relative -top-3">
              <Paragraph variant={VARIANT.MEDIUM}>
                {t('personalized_calendar')}
              </Paragraph>
              <button className="flex items-center gap-3 py-2 px-6 bg-primary-100 border border-primary-70 rounded-lg text-white mt-3 w-max" onClick={() => {
          const eventParams = {
            Crop_Name: crop?.name,
            Crop_ID: crop?.id
          };
          trackCustomEvent(CustomEventTypes.CROP_GUIDE_ADD_SOWING_DATE_CLICKED, JSON.stringify(eventParams));
          setShowSowingModal(true);
        }}>
                <AddDate className="stroke-white fill-transparent" width={21} height={22} />
                <SubHeading>{t('add_sowing_date')}</SubHeading>
              </button>
            </div>}
          {isLoading ? <Spinner /> : <div className="-mt-3">
              {cropStages.map((stage, index) => <CropStageAccordian sowingDate={sowingDate} key={stage.stage} cropStage={stage} index={index + 1} image={CropStageImage} cropActions={cropActions.filter(({
          crop_stage
        }) => crop_stage === stage.stage)} onClick={() => setShowSowingModal(true)} cropId={crop.id} currentAction={currentAction} fromApp={fromApp} />)}
            </div>}

          <p className="text-2xl mt-20 text-neutral-30 leading-8 text-center px-20 font-nato-semibold">
            {t('get_high_yield')}
          </p>
          <ErrorModal {...errorModalState} onClose={hideErrorModal}>
            <Okay handleClick={hideErrorModal} />
          </ErrorModal>
        </section>}
    </main> : null;
};
export const getServerSideProps: GetServerSideProps = async ({
  req: {
    cookies
  },
  locale,
  query
}) => {
  const accessToken = cookies[ACCESS_TOKEN_KEY];
  const fromApp = Boolean(cookies[NAVIGATION_FROM_APP]);
  let currentCrop: CropModel | null = null;
  if (!accessToken) {
    return {
      props: {
        title: 'Crop Guide',
        crops: [],
        fromApp,
        currentCrop
      }
    };
  }
  try {
    const userCrops = await fetchCropsV2(locale, 2, accessToken);
    const selectedCrops = userCrops.length > 0 ? userCrops[0].crops.filter(crop => crop.is_khetibook_enabled) : [];
    if (query.crop && userCrops.length > 0) {
      const crop = userCrops[0].crops.find(crop => crop.id === Number(query.crop));
      if (crop) {
        currentCrop = crop;
      }
    } else if (selectedCrops.length > 0) {
      currentCrop = selectedCrops[0];
    }
    return {
      props: {
        title: `${currentCrop ? currentCrop.app_display_name : ''} Crop Guide`,
        crops: currentCrop ? [currentCrop, ...selectedCrops.filter(crop => crop.id !== currentCrop?.id)] : selectedCrops,
        fromApp,
        currentCrop
      }
    };
  } catch {
    return {
      redirect: {
        destination: '/500',
        permanent: false
      }
    };
  }
};
export default CropGuidePage;